import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Stack, TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type {
  Operator, StickerHeaderConfig, StickerHeaderLine, StickerHeaderTemplate,
} from '../../api/types';
import {
  useCreateStickerHeaderTemplate, useUpdateStickerHeaderTemplate,
} from '../../api/queries';

/**
 * Header-template designer. Two halves:
 *   - Logo: pick the source (none / arms / operator / custom). For
 *     'operator' you pick which operator's logo. For 'custom' you
 *     upload a file. Logo height + alignment are global.
 *   - Lines: a vertical list of styled text lines. Each line carries
 *     its own font / size / bold / italic / colour / alignment.
 *
 * Submission goes via multipart so the optional logo file rides with
 * the structured fields. The header config is serialised to the
 * `headerConfig` form field as JSON.
 */
const FONT_FAMILIES = ['Helvetica', 'Times', 'Courier'] as const;
const ALIGNMENTS    = ['left', 'center', 'right'] as const;

function emptyLine(): StickerHeaderLine {
  return { text: '', fontFamily: 'Helvetica', fontSize: 10, bold: false,
           italic: false, color: '#000000', align: 'center' };
}

export function HeaderTemplateDialog({
  open, onClose, template, operators,
}: {
  open: boolean;
  onClose: () => void;
  template: StickerHeaderTemplate | null;
  operators: Operator[];
}) {
  const create = useCreateStickerHeaderTemplate();
  const update = useUpdateStickerHeaderTemplate();
  const isEdit = !!template;

  const [name, setName]                   = useState('');
  const [logoSource, setLogoSource]       = useState<'none' | 'arms' | 'operator' | 'custom'>('none');
  const [logoOperatorId, setOperator]     = useState<string>('');
  const [logoFile, setLogoFile]           = useState<File | null>(null);
  const [logoHeightMm, setLogoHeightMm]   = useState<number>(8);
  const [logoAlign, setLogoAlign]         = useState<'left' | 'center' | 'right'>('center');
  const [lines, setLines]                 = useState<StickerHeaderLine[]>([emptyLine()]);
  const [notes, setNotes]                 = useState('');

  useEffect(() => {
    if (!open) {
      // Reset when dialog closes — including mutation state.
      setName(''); setLogoSource('none'); setOperator(''); setLogoFile(null);
      setLogoHeightMm(8); setLogoAlign('center');
      setLines([emptyLine()]); setNotes('');
      create.reset(); update.reset();
      return;
    }
    if (template) {
      setName(template.name);
      setLogoSource(template.logoSource);
      setOperator(template.logoOperatorId ?? '');
      setNotes(template.notes ?? '');
      setLogoFile(null);
      try {
        const cfg: StickerHeaderConfig = JSON.parse(template.headerConfig || '{}');
        setLogoHeightMm(cfg.logoHeightMm ?? 8);
        setLogoAlign(cfg.logoAlign ?? 'center');
        setLines((cfg.lines && cfg.lines.length > 0) ? cfg.lines : [emptyLine()]);
      } catch {
        setLogoHeightMm(8); setLogoAlign('center'); setLines([emptyLine()]);
      }
    }
    // Mutation refs are unstable per render — including them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const headerConfig: StickerHeaderConfig = {
    logoHeightMm: logoSource === 'none' ? 0 : logoHeightMm,
    logoAlign,
    lines: lines.filter((l) => (l.text ?? '').length > 0),
  };

  const valid = name.trim().length > 0
    && (logoSource !== 'operator' || !!logoOperatorId)
    && (logoSource !== 'custom' || isEdit || !!logoFile);

  async function submit() {
    const values = {
      name: name.trim(),
      headerConfigJson: JSON.stringify(headerConfig),
      logoSource,
      logoOperatorId: logoSource === 'operator' ? logoOperatorId : null,
      logoFile: logoSource === 'custom' ? logoFile : null,
      notes: notes.trim() || null,
    };
    if (isEdit && template) {
      await update.mutateAsync({ id: template.id, values });
    } else {
      await create.mutateAsync(values);
    }
    onClose();
  }

  function setLine(idx: number, patch: Partial<StickerHeaderLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() { setLines((prev) => [...prev, emptyLine()]); }
  function removeLine(idx: number) {
    setLines((prev) => prev.length === 1 ? [emptyLine()] : prev.filter((_, i) => i !== idx));
  }

  const error = create.error ?? update.error;
  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        {isEdit ? `Edit "${template?.name}"` : 'New header template'}
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField label="Template name" value={name} onChange={(e) => setName(e.target.value)}
            required fullWidth size="small" />

          <Typography variant="overline" color="text.secondary">Logo</Typography>
          <Stack direction="row" spacing={1}>
            <TextField select label="Source" value={logoSource} size="small" sx={{ flex: 1 }}
              onChange={(e) => setLogoSource(e.target.value as typeof logoSource)}>
              <MenuItem value="none">None (text-only header)</MenuItem>
              <MenuItem value="arms">ARMS logo (bundled)</MenuItem>
              <MenuItem value="operator">Operator logo</MenuItem>
              <MenuItem value="custom">Custom upload</MenuItem>
            </TextField>
            {logoSource === 'operator' && (
              <TextField select label="Operator" value={logoOperatorId} size="small" sx={{ flex: 1 }}
                onChange={(e) => setOperator(e.target.value)} required>
                {operators.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.displayName}</MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {logoSource === 'custom' && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" component="label" size="small">
                {logoFile ? `Replace (${logoFile.name})` : (isEdit ? 'Replace logo file' : 'Choose logo file')}
                <input hidden type="file" accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
              </Button>
              {isEdit && !logoFile && (
                <Typography variant="caption" color="text.secondary">
                  Existing logo kept unless you upload a new file.
                </Typography>
              )}
            </Stack>
          )}

          {logoSource !== 'none' && (
            <Stack direction="row" spacing={1}>
              <TextField label="Logo height (mm)" type="number" value={logoHeightMm} size="small"
                onChange={(e) => setLogoHeightMm(parseFloat(e.target.value || '0'))}
                inputProps={{ min: 0, step: 0.5 }} sx={{ flex: 1 }} />
              <TextField select label="Logo alignment" value={logoAlign} size="small"
                onChange={(e) => setLogoAlign(e.target.value as typeof logoAlign)} sx={{ flex: 1 }}>
                {ALIGNMENTS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Stack>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="overline" color="text.secondary">Text lines</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          </Box>

          {lines.map((line, idx) => (
            <Box key={idx} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField label={`Line ${idx + 1}`} value={line.text ?? ''} size="small" sx={{ flex: 1 }}
                    onChange={(e) => setLine(idx, { text: e.target.value })} />
                  <IconButton size="small" color="error" onClick={() => removeLine(idx)}
                    disabled={lines.length === 1 && !line.text}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField select label="Font" value={line.fontFamily ?? 'Helvetica'} size="small" sx={{ flex: 1 }}
                    onChange={(e) => setLine(idx, { fontFamily: e.target.value })}>
                    {FONT_FAMILIES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                  <TextField label="Size (pt)" type="number" value={line.fontSize ?? 10} size="small" sx={{ flex: 0.7 }}
                    onChange={(e) => setLine(idx, { fontSize: parseFloat(e.target.value || '0') })}
                    inputProps={{ min: 4, max: 24, step: 0.5 }} />
                  <TextField label="Colour" value={line.color ?? '#000000'} size="small" sx={{ flex: 0.7 }}
                    onChange={(e) => setLine(idx, { color: e.target.value })} />
                  <TextField select label="Align" value={line.align ?? 'center'} size="small" sx={{ flex: 0.6 }}
                    onChange={(e) => setLine(idx, { align: e.target.value as 'left' | 'center' | 'right' })}>
                    {ALIGNMENTS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={<Checkbox checked={!!line.bold}   onChange={(e) => setLine(idx, { bold: e.target.checked })} />}
                    label="Bold" />
                  <FormControlLabel
                    control={<Checkbox checked={!!line.italic} onChange={(e) => setLine(idx, { italic: e.target.checked })} />}
                    label="Italic" />
                </Stack>
              </Stack>
            </Box>
          ))}

          <TextField label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
            multiline minRows={2} fullWidth size="small" />

          {error && (
            <ErrorAlert>
              {/* Always have *some* text so the user sees what failed —
                  empty Alerts (the "pink line, no message" footgun)
                  are forbidden by project rules. */}
              {(error as Error).message
                || (error as { code?: string }).code
                || String(error)
                || 'Saving the template failed (no further detail from server).'}
            </ErrorAlert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button variant="contained" disabled={!valid || isPending} onClick={submit}>
          {isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Create template')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
